<template>
    <div>
        <!-- Host Buttons -->
        <HostButtons v-if="isHost" @record="onRecord" />
        <!-- /Host Buttons -->

        <b-alert
            v-for="(error, index) in errors"
            v-bind:key="index"
            variant="danger"
            dismissible
            show
        >
            {{ error }}
        </b-alert>

        <div v-if="showConnecting" style="width: 100%; text-align: center">
            <div>Connecting to signal server...</div>
            <b-spinner large label="Connecting to signal server..." />
        </div>

        <!-- Participants -->
        <div class="row no-gutters" v-if="initialised">
            <div class="col-md-6 p-2">
                <ParticipantLocal
                    :data="{
                        userData: {
                            displayName: $store.getters['user'].displayName,
                        },
                    }"
                    @audioTrack="setAudioTrack"
                    @videoTrack="setVideoTrack"
                    @micCut="setMicCut"
                    :micCut="isMicCut"
                />
            </div>

            <div
                class="col-md-6 p-2"
                v-for="participant in participants"
                :key="participant.peer_id"
            >
                <ParticipantRemote
                    :data="participant"
                    @toggleRemoteMicCut="
                        toggleRemoteMicCut(participant.peer_id)
                    "
                    @drop="reloadRemotePeer(participant.peer_id)"
                />
            </div>
        </div>
        <!-- /Participants -->
    </div>
</template>

<script>
import {getParser} from 'bowser';
const agent = getParser(window.navigator.userAgent);

import ParticipantsStore from "../store/participants.store";
import RecorderStore from "../store/recorder.store";

import ParticipantLocal from "@/components/ParticipantLocal";
import ParticipantRemote from "@/components/ParticipantRemote";
import HostButtons from "@/components/HostButtons";

import {
    createAudioContext,
    testAudioContext,
    createMediaStream,
    hasMediaRecorder,
} from "../modules/MediaHelpers";
import WebRTCController from "../webrtc/WebRTCController";
import SignalServer from "../services/SignalServer";

export default {
    name: "LiveSession",
    data() {
        return {
            errors: [],
            initialised: false,
            audioTrack: null,
            videoTrack: null,
            isMicCut: false,

            isSignalServerConnected: false,
            peerId: null,
        };
    },

    computed: {
        isHost() {
            return this.$store.getters["auth/isHost"];
        },

        participants() {
            return this.$store.getters["participants/allParticipants"];
        },

        sessionId() {
            return this.$store.state.sessionId;
        },

        hostNumber() {
            return this.$store.state.hostNumber;
        },

        showConnecting() {
            return !this.isSignalServerConnected;
        },
    },

    methods: {
        setError(error) {
            this.errors.push(error);
            this.sendPeerStatus({
                error: error,
            });
        },

        setAudioTrack(track) {
            this.audioTrack = track;

            setTimeout(() => {
                this.audioTrack.onmute = this.onAudioTrackMuted;
            }, 2000);

            if (this.webRTCController)
                this.webRTCController.outgoingAudioTrack = track;
            this.sendPeerStatus({
                audioDevice: track.label,
            });
        },

        onAudioTrackMuted() {
            this.setError(
                `Your microphone has been unintentionally muted. Please ensure another app or tab isn't trying to use it, then re-join the session`
            );
        },

        setVideoTrack(track) {
            this.videoTrack = track;
            if (this.webRTCController)
                this.webRTCController.outgoingVideoTrack = track;
            this.sendPeerStatus({
                videoDevice: track.label,
            });
        },

        setMicCut(isMicCut) {
            if (this.recordingController)
                this.recordingController.cutAudio(isMicCut);
            this.sendPeerStatus({
                isMicCut: isMicCut,
            });
        },

        onRecord(isStartRecorder) {
            if (!this.signalServer) return;
            if (isStartRecorder === true)
                this.signalServer.signal("START_RECORDING");
            if (isStartRecorder === false)
                this.signalServer.signal("STOP_RECORDING");
        },

        sendPeerStatus(items) {
            if (!this.signalServer) return;
            this.signalServer.signal("PEER_STATUS", items);
        },

        updatePeerStatus(data) {
            const { peer_id } = data;
            this.$store.commit("participants/SET_PEERSTATE", {
                peer_id: peer_id,
                value: { ...data },
            });
        },

        async startRecording() {
            try {
                await this.recordingController.start({
                    sessionId: this.sessionId,
                    stream: createMediaStream(
                        [this.audioTrack, this.videoTrack],
                        true
                    ),
                });
            } catch (err) {
                this.setError(`Unable to start recording. ${err.message}`);
            }
        },

        stopRecording() {
            try {
                this.recordingController.stop();
            } catch (err) {
                this.setError(`Unable to stop recording. ${err.message}`);
            }
        },

        toggleMic() {
            this.isMicCut = !this.isMicCut;
        },

        toggleRemoteMicCut(peerId) {
            if (!this.signalServer) return;
            this.signalServer.signal("TOGGLE_MIC", { peer_id: peerId });
        },

        reloadRemotePeer(peerId) {
            if (!this.signalServer) return;
            this.signalServer.signal("RELOAD", { peer_id: peerId });
        },

        async initialiseMedia() {
            let audioContext = null;

            try {
                audioContext = await createAudioContext();
                this.$store.commit("AUDIO_CONTEXT", audioContext);
            } catch (err) {
                this.setError(
                    "Unable to initialise audio context. Possibly because permission was denied."
                );
                return;
            }

            try {
                await testAudioContext(audioContext);
            } catch (err) {
                this.setError(err.message);
                return;
            }
        },

        initialiseSignalServer() {
            this.signalServer = new SignalServer({
                name: "Signal Server",
                server: process.env.VUE_APP_SIGNAL_SERVER,
                path: process.env.VUE_APP_SIGNAL_PATH,
            });

            this.signalServer.connect(this.$store.getters["token"]);

            this.signalServer.on("AUTHORISED", () => {
                this.isSignalServerConnected = true;
                if (!this.peerId) this.peerId = this.signalServer.id;

                let joinData = {
                    peerId: this.peerId,
                    sessionId: this.sessionId,
                    hostNumber: this.hostNumber,
                };

                this.signalServer.signal("JOIN", joinData);
                this.sendPeerStatus({
                    version: process.env.VUE_APP_VERSION,
                    browser: agent.getBrowser(),
                    audioDevice: this.audioTrack && this.audioTrack.label,
                    isRecording:
                        this.recordingController &&
                        this.recordingController.isRecording,
                });
            });

            this.signalServer.on("UNAUTHORISED", () => {
                this.isSignalServerConnected = false;
                this.setError("Unauthorised to connect to signal server");
            });

            this.signalServer.on("DISCONNECTED", () => {
                this.isSignalServerConnected = false;
            });

            this.signalServer.on("JOINED_SESSION", (data) => {
                this.joinedToSession = true;
                this.sessionData = data;
                this.$store.commit("SESSION_NAME", data.sessionName);
            });

            this.signalServer.on("PEER_STATUS", (data) => {
                this.updatePeerStatus(data);
            });

            this.signalServer.on("START_RECORDING", () => {
                this.startRecording();
            });

            this.signalServer.on("STOP_RECORDING", () => {
                this.stopRecording();
            });

            this.signalServer.on("TOGGLE_MIC", () => {
                this.toggleMic();
            });

            this.signalServer.on("RELOAD", () => {
                window.location.reload();
            });
        },

        initialiseWebRTC() {
            this.webRTCController = new WebRTCController(
                this.signalServer,
                {},
                this.$store.state.allowVideo
            );
            this.webRTCController.on("NEW_PEER", (data) => {
                this.$store.dispatch("participants/addParticipant", {
                    type: "remote",
                    peer_id: data.peer_id,
                    userData: data.userData,
                    mediaSource: data.stream,
                    playerMuted: false,
                    stats: {},
                });

                this.signalServer.signal("GET_PEER_STATE", {
                    peer_id: data.peer_id,
                });
            });

            this.webRTCController.on("REMOVE_PEER", (data) => {
                this.$store.dispatch(
                    "participants/removeParticipant",
                    data.peer_id
                );
            });

            this.webRTCController.on("STATS", (data) => {
                this.$store.commit("participants/SET_PEERSTATS", data);
            });
        },

        async initialiseRecording() {
            this.$store.registerModule("recorder", RecorderStore);
            this.recordingController = this.$store.state.recorder.recordingController;

            this.recordingController.on("RECORDING", (state) => {
                this.$store.commit("IS_RECORDING", state);
                this.sendPeerStatus({
                    isRecording: state,
                });
            });

            try {
                await this.recordingController.connect(
                    this.$store.getters["token"]
                );
            } catch (err) {
                this.setError(
                    `Unable to connect to record server. ${err.message}`
                );
            }
        },
    },

    components: {
        ParticipantLocal,
        ParticipantRemote,
        HostButtons,
    },

    async beforeMount() {
        this.$store.registerModule("participants", ParticipantsStore);

        await this.initialiseMedia(); // initialise before recorder
        this.initialiseRecording(); // initialise before signalserver
        this.initialiseSignalServer(); // initialise before WebRTC
        this.initialiseWebRTC();

        if (!hasMediaRecorder()) {
            this.setError(
                "Your browser doesn't support recording, please update to the latest version. (we recommend Chrome on desktop)"
            );
        }

        this.initialised = true;
    },
};
</script>